import { makeAutoObservable, runInAction } from "mobx";
import {
  OrderInterface,
  OrdersStatus,
  OrderStatus,
  OrderType,
} from "../interfaces_and_types";
import {
  getOrders,
  updateOrder,
  UpdateOrderItem,
  getOrdersStatus,
  setEstimatedTime,
  UpdateOrderPickedUp,
  reportPickupNoShow,
} from "../services";

// const checkIfOrderStatus = (obj: OrdersStatus | {}): obj is OrdersStatus => {
//   if ((obj as OrdersStatus).isPreparing >= 0) {
//     return true;
//   }
//   return false;
// };

// const checkIfOrder = (obj: OrderType | {}): obj is OrderType => {
//   if ((obj as OrderType).price >= 0) {
//     return true;
//   }
//   return false;
// };

// The numeric status is canonical (it's what the backend transition endpoints
// write); the booleans are what the queue getters filter on. Deriving the
// booleans keeps an order from disappearing from every tab when a payload
// carries a status with stale or missing flags.
const durationMinutes = (time: string | null | undefined): number | null => {
  if (time == null) return null;
  const match = /^(\d+):([0-5]?\d):([0-5]?\d)/.exec(time.trim());
  if (match === null) return null;
  return (
    parseInt(match[1], 10) * 60 +
    parseInt(match[2], 10) +
    parseInt(match[3], 10) / 60
  );
};

const syncFlagsFromStatus = (order: OrderType) => {
  if (order.status == null) return;
  order.isWaiting = order.status <= OrderStatus.SENT_TO_RESTAURANT;
  order.isPreparing = order.status === OrderStatus.PREPARING;
  order.isReady =
    order.status >= OrderStatus.PREPARED &&
    order.status !== OrderStatus.CANCELLED;
  order.refused = order.status === OrderStatus.CANCELLED;
};
export class OrdersStore implements OrderInterface {
  orders: OrderType[] | undefined = [];
  ordersStatus: OrdersStatus = {
    isWaiting: 0,
    isReady: 0,
    isPreparing: 0,
    refused: 0,
  };
  oldOrders: OrderType[] | undefined = [];
  ordersFetched: boolean = false;
  oldOrdersFetched: boolean = false;
  ordersStatusFetched: boolean = false;
  /** A push arrived before the auth token was loaded — refetch once it is. */
  pendingPushRefetch: boolean = false;
  oldOrdersPage = {
    page: 1,
    scrollTop: 0,
  };
  waitingReadyOrders: OrderType[] | undefined = [];

  // History filters (shared between AppHeader and OrdersHistory)
  historyFilter: "all" | "done" | "cancelled" = "all";
  historyDateFrom: string = new Date().toISOString().split("T")[0];
  historyDateTo: string = new Date().toISOString().split("T")[0];

  setHistoryFilter = (filter: "all" | "done" | "cancelled") => {
    this.historyFilter = filter;
  };

  setHistoryDateFrom = (date: string) => {
    this.historyDateFrom = date;
    if (date > this.historyDateTo) this.historyDateTo = date;
  };

  setHistoryDateTo = (date: string) => {
    this.historyDateTo = date;
    if (date < this.historyDateFrom) this.historyDateFrom = date;
  };

  updateOrderPickUp = async (id: number, token: string) => {
    const res = await UpdateOrderPickedUp(id, token);
    runInAction(() => {
      if (res.status !== 200) return;

      const completeLocal = (order: OrderType) => {
        order.status = OrderStatus.COMPLETED;
        syncFlagsFromStatus(order);
        this.oldOrders !== undefined && this.oldOrders.unshift(order);
      };

      if (this.waitingReadyOrders !== undefined) {
        const orderIndex = this.waitingReadyOrders.findIndex(
          (o) => o.id === id,
        );
        if (orderIndex !== -1) {
          const [order] = this.waitingReadyOrders.splice(orderIndex, 1);
          // Mirror what the backend writes for a self-pickup handover
          // (COMPLETED, not PICKEDUP) so the local row doesn't disagree with
          // the next refetch or push.
          completeLocal(order);
        }
      }

      // Ready-column OrderCard also completes cash pickup from the kanban.
      if (this.orders !== undefined) {
        const idx = this.orders.findIndex((o) => o.id === id);
        if (idx !== -1) {
          const [order] = this.orders.splice(idx, 1);
          if (order.isReady) this.isReadyDecrement();
          completeLocal(order);
        }
      }
    });
    return res;
  };

  reportNoShow = async (
    id: number,
    token: string,
    reason: "customer_no_show" | "refuse_to_pay" = "customer_no_show",
  ) => {
    return await reportPickupNoShow(id, token, reason);
  };

  updateEstimatedTime = async (id: number, time: string, token: string) => {
    const order = this.orders?.find((o) => o.id === id);
    if (order) {
      order.estimatedTime = time;
    }
    setEstimatedTime(id, time, token).catch((e) => {
      console.error(e);
    });
  };

  changeOldOrdersPage = (scrollTop: number) => {
    this.oldOrdersPage.page++;
    runInAction(() => (this.oldOrdersPage.scrollTop = scrollTop));
  };
  changeOrdersStatusFetched = (bool: boolean) =>
    (this.ordersStatusFetched = bool);
  changeOrdersFetched = (bool: boolean) => (this.ordersFetched = bool);
  changeOldOrdersFetched = (bool: boolean) => (this.oldOrdersFetched = bool);
  setPendingPushRefetch = (bool: boolean) => (this.pendingPushRefetch = bool);

  fetchOldOrders = async (token: string, lastID: number) => {
    const data = await getOrders(true, lastID, token);
    runInAction(() => {
      if (data.status === 200) {
        this.setOldOrders(data.orders);
      }
    });
  };

  clearOldOrders = () => {
    this.oldOrders = [];
    this.oldOrdersFetched = false;
  };

  fetchOrders = async (token: string) => {
    const data = await getOrders(false, 0, token);
    console.log(data);
    runInAction(() => {
      if (data.status === 200) {
        const waiting_orders: OrderType[] = [];
        const current_orders: OrderType[] = [];
        //this.setOrders(data.orders);
        data.orders.forEach((o) => {
          syncFlagsFromStatus(o);
          o.status === OrderStatus.PREPARED
            ? waiting_orders.push(o)
            : o.status < OrderStatus.PREPARED && current_orders.push(o);
        });
        this.setOrders(current_orders);
        this.setWaitingReadyOrders(waiting_orders);
      }
    });
  };

  fetchOrdersStatus = async (token: string) => {
    const data = await getOrdersStatus(token);
    runInAction(() => {
      if (data.status === 200) {
        this.isPreparingInsert(data.isPreparing);
        this.isWaitingInsert(data.isWaiting);
        this.isReadyInsert(data.isReady);
        this.refusedInsert(data.refused);
      }
    });
  };

  update_order_from_notification = (incoming: Partial<OrderType>) => {
    // Find existing order across all queues
    const existing =
      this.orders?.find((o) => o.id === incoming.id) ??
      this.waitingReadyOrders?.find((o) => o.id === incoming.id) ??
      this.oldOrders?.find((o) => o.id === incoming.id);

    // A late/stale push still carries the Order model's 20-minute default.
    // Don't let that clobber a kitchen-selected prep time already on the card.
    const patch = { ...incoming };
    const localMins = durationMinutes(existing?.estimatedTime);
    const incomingMins = durationMinutes(patch.estimatedTime);
    if (
      localMins != null &&
      incomingMins === 20 &&
      localMins !== incomingMins
    ) {
      delete patch.estimatedTime;
    }

    // Merge incoming fields onto existing order, preserving rich data
    const order: OrderType = existing
      ? Object.assign(existing, patch)
      : (patch as OrderType);

    // Without a status the order can't be bucketed into any queue
    if (order.status == null) return;
    syncFlagsFromStatus(order);

    const current_order_index = this.orders?.findIndex(
      (o) => o.id === order.id,
    );
    const waiting_done_order_index = this.waitingReadyOrders?.findIndex(
      (o) => o.id === order.id,
    );
    const old_order_index = this.oldOrders?.findIndex((o) => o.id === order.id);

    runInAction(() => {
      if (order.status <= OrderStatus.PREPARING) {
        waiting_done_order_index !== undefined &&
          waiting_done_order_index !== -1 &&
          this.waitingReadyOrders?.splice(waiting_done_order_index, 1);
        old_order_index !== undefined &&
          old_order_index !== -1 &&
          this.oldOrders?.splice(old_order_index, 1);
        current_order_index !== undefined && current_order_index !== -1
          ? this.orders?.splice(current_order_index, 1, order)
          : this.insertToOrders(order);
      } else if (order.status === OrderStatus.PREPARED) {
        // Ready pillar: food is on the counter, nobody has collected it yet.
        // PICKEDUP (courier took it, driverStatus=PICKEDUP_BY_DRIVER),
        // COMPLETED and CANCELLED all fall through to history below — the
        // restaurant is done with the order the moment it is handed over.
        old_order_index !== undefined &&
          old_order_index !== -1 &&
          this.oldOrders?.splice(old_order_index, 1);
        current_order_index !== undefined &&
          current_order_index !== -1 &&
          this.orders?.splice(current_order_index, 1);
        waiting_done_order_index !== undefined &&
        waiting_done_order_index !== -1
          ? this.waitingReadyOrders?.splice(waiting_done_order_index, 1, order)
          : this.insertToWaitingDoneOrders(order);
      } else if (order.status > OrderStatus.PREPARING) {
        current_order_index !== undefined &&
          current_order_index !== -1 &&
          this.orders?.splice(current_order_index, 1);
        waiting_done_order_index !== undefined &&
          waiting_done_order_index !== -1 &&
          this.waitingReadyOrders?.splice(waiting_done_order_index, 1);
        old_order_index !== undefined && old_order_index !== -1
          ? this.oldOrders?.splice(old_order_index, 1, order)
          : this.insertToOldOrders(order);
      }
    });
  };

  insertToOrders = (order: OrderType) => {
    if (this.orders !== undefined) {
      this.orders.push(order);
      runInAction(() => {
        order.status <= OrderStatus.SENT_TO_RESTAURANT
          ? this.isWaitingIncrement()
          : this.isPreparingIncrement();
      });
    }
  };

  insertToWaitingDoneOrders = (order: OrderType) => {
    if (this.waitingReadyOrders !== undefined) {
      this.waitingReadyOrders.push(order);
    }
  };

  insertToOldOrders = (order: OrderType) => {
    if (this.oldOrders !== undefined) {
      this.oldOrders.push(order);
      runInAction(() => {
        order.status === OrderStatus.CANCELLED
          ? this.refusedIncrement()
          : this.isReadyIncrement();
      });
    }
  };

  removeOrders = () => (this.orders = []);
  removeOldOrders = () => (this.oldOrders = []);
  setOldOrders = (oldOrders: OrderType[]) => {
    const existingIds = new Set(this.oldOrders?.map((o) => o.id));
    const newOrders = oldOrders.filter((o) => !existingIds.has(o.id));
    this.oldOrders = (this.oldOrders ?? []).concat(newOrders);
  };
  setOrders = (orders: OrderType[]) => (this.orders = orders);
  setWaitingReadyOrders = (orders: OrderType[]) =>
    (this.waitingReadyOrders = orders);
  isWaitingIncrement = () => this.ordersStatus.isWaiting++;
  isWaitingInsert = (val: number) => (this.ordersStatus.isWaiting = val);
  isWaitingDecrement = () => this.ordersStatus.isWaiting--;
  isWaitingReset = () => (this.ordersStatus.isWaiting = 0);
  isReadyIncrement = () => this.ordersStatus.isReady++;
  isReadyInsert = (val: number) => (this.ordersStatus.isReady = val);
  isReadyDecrement = () => this.ordersStatus.isReady--;
  isReadyReset = () => (this.ordersStatus.isReady = 0);
  isPreparingIncrement = () => this.ordersStatus.isPreparing++;
  isPreparingInsert = (val: number) => (this.ordersStatus.isPreparing = val);
  isPreparingDecrement = () => this.ordersStatus.isPreparing--;
  isPreparingReset = () => (this.ordersStatus.isPreparing = 0);
  refusedIncrement = () => this.ordersStatus.refused++;
  refusedInsert = (val: number) => (this.ordersStatus.refused = val);
  refusedDecrement = () => this.ordersStatus.refused--;
  refusedReset = () => (this.ordersStatus.refused = 0);
  private updateOrderStatus = (
    isPreparing: number,
    refused: number,
    order: OrderType,
    status: OrderStatus,
  ) => {
    if (order !== undefined) {
      order.isPreparing = isPreparing === 1 ? true : false;
      order.refused = refused === 1 ? true : false;
      order.isWaiting = false;
      order.status = status;
      console.log(JSON.stringify(this.orders));
    }
  };

  updateOrder = async (
    orderID: number,
    updateType: number,
    token: string,
    estimatedTime?: string,
  ) => {
    if (this.orders !== undefined) {
      const order = this.orders.find((ord) => ord.id === orderID) ?? null;
      // Optimistic: the countdown anchors on estimatedTime, so set it before
      // the round-trip. Sending it on the same PREPARING request is what
      // stops the backend's 20-minute default from winning a race.
      if (order && estimatedTime) {
        order.estimatedTime = estimatedTime;
      }
      const res = await updateOrder(orderID, updateType, token, estimatedTime);
      const index = this.orders.findIndex((ord) => ord.id === orderID);
      let status = OrderStatus.CREATED;
      console.log(order);

      runInAction(() => {
        if (res.status === 200 && this.orders !== undefined) {
          if (res.isPreparing === 1) {
            this.isWaitingDecrement();
            this.isPreparingIncrement();
            status = OrderStatus.PREPARING;
          } else if (
            res.refused === 1 &&
            !!order &&
            order.isPreparing &&
            order.status === OrderStatus.PREPARING
          ) {
            this.isPreparingDecrement();
            this.refusedIncrement();
            this.oldOrders !== undefined &&
              this.oldOrdersFetched &&
              this.oldOrders.unshift(order);
            this.orders.splice(index, 1);
            status = OrderStatus.CANCELLED;
          } else if (
            res.refused === 1 &&
            !!order &&
            order.isWaiting &&
            (order.status === OrderStatus.CREATED ||
              order.status === OrderStatus.SENT_TO_RESTAURANT)
          ) {
            this.isWaitingDecrement();
            this.refusedIncrement();
            this.oldOrders !== undefined &&
              this.oldOrdersFetched &&
              this.oldOrders.unshift(order);
            this.orders.splice(index, 1);
            status = OrderStatus.CANCELLED;
          }
          order &&
            this.updateOrderStatus(res.isPreparing, res.refused, order, status);
        }
      });
    }
  };

  updateOrderItem = async (
    orderID: number,
    itemOrderID: number,
    token: string,
  ) => {
    if (this.orders !== undefined) {
      const orderIndex = this.orders.findIndex((ord) => ord.id === orderID);
      const order = this.orders[orderIndex];
      const orderItemIndex = order.items.findIndex(
        (item) => item.itemOrderID === itemOrderID,
      );
      const item = order.items[orderItemIndex];
      runInAction(() => {
        item.isReady = true;
      });
      const data = await UpdateOrderItem(orderID, itemOrderID, token);
      if (data.orderReady) order.status = OrderStatus.PREPARED;
      return data.status === 200 ? data.orderReady : data.status;
    }
  };

  get waitingOrders(): OrderType[] {
    return (this.orders ?? []).filter((o) => o.isWaiting);
  }

  get preparingOrders(): OrderType[] {
    return (this.orders ?? []).filter((o) => o.isPreparing);
  }

  get readyOrders(): OrderType[] {
    return this.waitingReadyOrders ?? [];
  }

  get sortedOldOrders(): OrderType[] {
    return (this.oldOrders ?? [])
      .slice()
      .sort(
        (a, b) =>
          new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime(),
      );
  }

  canDropToQueue = (
    order: OrderType,
    targetQueue: "preparing" | "ready",
  ): boolean => {
    if (targetQueue === "preparing") {
      return order.isWaiting;
    }
    if (targetQueue === "ready") {
      return order.isPreparing;
    }
    return false;
  };

  dragToPreparing = async (
    orderId: number,
    token: string,
    estimatedTime: string,
  ) => {
    if (this.orders === undefined) return;
    const existing = this.orders.find((o) => o.id === orderId);
    if (existing) {
      existing.estimatedTime = estimatedTime;
    }
    const res = await updateOrder(
      orderId,
      OrderStatus.PREPARING,
      token,
      estimatedTime,
    );
    runInAction(() => {
      if (res.status === 200 && this.orders !== undefined) {
        const order = this.orders.find((o) => o.id === orderId);
        if (order) {
          order.isWaiting = false;
          order.isPreparing = true;
          order.status = OrderStatus.PREPARING;
          order.estimatedTime = estimatedTime;
          this.isWaitingDecrement();
          this.isPreparingIncrement();
        }
      }
    });
  };

  dragToReady = async (orderId: number, token: string) => {
    if (this.orders === undefined || this.waitingReadyOrders === undefined)
      return;
    const order = this.orders.find((o) => o.id === orderId);
    if (!order) return;

    const unreadyItems = (order.items ?? []).filter((item) => !item.isReady);
    await Promise.all(
      unreadyItems.map((item) =>
        UpdateOrderItem(orderId, item.itemOrderID, token),
      ),
    );

    runInAction(() => {
      if (this.orders === undefined || this.waitingReadyOrders === undefined)
        return;
      const orderIndex = this.orders.findIndex((o) => o.id === orderId);
      if (orderIndex === -1) return;
      const ord = this.orders[orderIndex];

      ord.isPreparing = false;
      ord.isReady = true;
      ord.status = OrderStatus.PREPARED;
      (ord.items ?? []).forEach((item) => (item.isReady = true));

      this.isPreparingDecrement();
      this.isReadyIncrement();

      this.orders.splice(orderIndex, 1);
      this.waitingReadyOrders.push(ord);
    });
  };

  constructor() {
    makeAutoObservable(this);
  }
}

const ordersStore = new OrdersStore();
export default ordersStore;
